import { forwardRef } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { POSOrderWithItems } from '../../types/admin';

interface CustomerReceiptProps {
  order: POSOrderWithItems;
  shopName: string;
  shopAddress?: string;
  shopPhone?: string;
  shopTaxId?: string;
}

const paymentMethodLabels: Record<string, string> = {
  cash: 'Espèces / نقدي',
  card: 'Carte / بطاقة',
  check: 'Chèque / شيك',
  ccp: 'CCP',
  mobile: 'Mobile / محفظة',
};

export const CustomerReceipt = forwardRef<HTMLDivElement, CustomerReceiptProps>(
  ({ order, shopName, shopAddress, shopPhone, shopTaxId }, ref) => {
    const formatCurrency = (amount: number) => amount.toFixed(2) + ' DA';

    const subtotal = order.subtotal_amount || order.subtotal || 0;
    const fiscalStamp = order.fiscal_stamp || 0;

    return (
      <div ref={ref} className="bg-white p-8 max-w-[80mm] mx-auto font-mono text-sm text-black">
        {/* Header */}
        <div className="text-center border-b-2 border-black pb-4 mb-4">
          <h1 className="text-xl font-bold mb-2">{shopName}</h1>
          {shopAddress && <p className="text-xs">{shopAddress}</p>}
          {shopPhone && <p className="text-xs">Tél: {shopPhone}</p>}
          {shopTaxId && <p className="text-xs">NIF: {shopTaxId}</p>}
        </div>

        {/* Order Info */}
        <div className="mb-4 text-xs space-y-1">
          <div className="flex justify-between">
            <span>N° Commande:</span>
            <span className="font-bold">{order.order_number}</span>
          </div>
          <div className="flex justify-between">
            <span>Date:</span>
            <span>{format(new Date(order.created_at), 'd MMM yyyy HH:mm', { locale: fr })}</span>
          </div>
          {order.order_type && (
            <div className="flex justify-between">
              <span>Type:</span>
              <span className="capitalize">{order.order_type}</span>
            </div>
          )}
          {order.table_id && (
            <div className="flex justify-between">
              <span>Table:</span>
              <span>{order.table_id}</span>
            </div>
          )}
          {order.customer_name && (
            <div className="flex justify-between">
              <span>Client:</span>
              <span>{order.customer_name}</span>
            </div>
          )}
        </div>

        {/* Items */}
        <div className="border-t-2 border-dashed border-gray-400 pt-2 mb-4">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-400">
                <th className="text-left py-1">Article</th>
                <th className="text-center py-1">Qté</th>
                <th className="text-right py-1">Prix</th>
                <th className="text-right py-1">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item) => (
                <tr key={item.id} className="border-b border-gray-200">
                  <td className="py-2">
                    <div>{item.product_name}</div>
                    {item.notes && (
                      <div className="text-xs text-gray-600 italic">* {item.notes}</div>
                    )}
                  </td>
                  <td className="text-center">{item.quantity}</td>
                  <td className="text-right">{formatCurrency(item.unit_price)}</td>
                  <td className="text-right font-medium">{formatCurrency(item.total_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="border-t-2 border-black pt-2 space-y-1 text-xs">
          <div className="flex justify-between">
            <span>Sous-total / المجموع الفرعي:</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span>TVA:</span>
            <span>{formatCurrency(order.vat_amount)}</span>
          </div>
          {fiscalStamp > 0 && (
            <div className="flex justify-between">
              <span>Timbre fiscal / الطابع الجبائي:</span>
              <span>{formatCurrency(fiscalStamp)}</span>
            </div>
          )}
          {order.discount_amount > 0 && (
            <div className="flex justify-between text-red-600">
              <span>Remise / خصم:</span>
              <span>-{formatCurrency(order.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between text-lg font-bold border-t-2 border-black pt-2 mt-2">
            <span>TOTAL:</span>
            <span>{formatCurrency(order.total_amount)}</span>
          </div>
        </div>

        {/* Payment Info */}
        {order.payment_method && (
          <div className="border-t border-dashed border-gray-400 mt-3 pt-2 space-y-1 text-xs">
            <div className="flex justify-between">
              <span>Mode de paiement:</span>
              <span className="font-medium">
                {paymentMethodLabels[order.payment_method] || order.payment_method}
              </span>
            </div>
            {order.payment_method === 'cash' && order.payment_amount && (
              <>
                <div className="flex justify-between">
                  <span>Montant reçu / المبلغ المدفوع:</span>
                  <span className="font-medium">{formatCurrency(order.payment_amount)}</span>
                </div>
                {(order.change_amount || 0) > 0 && (
                  <div className="flex justify-between font-bold">
                    <span>Monnaie / الباقي:</span>
                    <span>{formatCurrency(order.change_amount || 0)}</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6 pt-4 border-t-2 border-dashed border-gray-400 text-xs">
          <p className="mb-2">Merci de votre visite!</p>
          <p className="mb-2">شكرا لزيارتكم</p>
          <p className="text-xs text-gray-600">
            {format(new Date(), 'd MMMM yyyy HH:mm', { locale: fr })}
          </p>
        </div>

        {/* Barcode placeholder */}
        <div className="text-center mt-4">
          <div className="inline-block px-4 py-1 bg-gray-100 font-mono text-xs">
            {order.order_number}
          </div>
        </div>
      </div>
    );
  }
);

CustomerReceipt.displayName = 'CustomerReceipt';
